/**
 * Learn more about theme events at
 * https://help.switchthemes.co/baseline/developer/theme-events/overview.html
 */

window.theme = window.theme || {};

window.theme.events = {
  version: '1.0',
};

document.addEventListener('theme:update:cart', async () => {
  document.dispatchEvent(new CustomEvent('baseline:cart:lock'));

  try {
    const response = await fetch(
      `${window.location.pathname}?sections=cart-items,cart-footer,cart-item-count,cart-live-region`
    );
    const data = await response.json();

    if (!data.status) {
      document.body.dispatchEvent(
        new CustomEvent('baseline:cart:update', {
          bubbles: true,
          detail: {
            response: {
              sections: {
                ...data,
              },
            },
          },
        })
      );
    }
  } catch (e) {
    console.error(e);
    document.getElementById('cart-errors').textContent =
      theme.strings.cartError;
    document.dispatchEvent(
      new CustomEvent('theme:cart:error:other', {
        detail: {
          message: theme.strings.cartError,
          error: e,
        },
      })
    );
  } finally {
    document.dispatchEvent(new CustomEvent('baseline:cart:unlock'));
  }
});

const getCartDrawerEl = () => {
  return document.querySelector('[data-cart-drawer]');
};

document.body.addEventListener('cart-will-open', async () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:opening', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-is-open', async () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:open', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-will-close', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closing', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-is-closed', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closed', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.addEventListener('theme:open:cart-drawer', () => {
  if (
    window.theme.settings.cartType === 'drawer' &&
    Alpine.store('modals').modals.cart
  ) {
    Alpine.store('modals').closeAll();

    Alpine.store('modals').open('cart');
  }
});

document.addEventListener('theme:close:cart-drawer', () => {
  if (
    window.theme.settings.cartType === 'drawer' &&
    Alpine.store('modals').modals.cart
  ) {
    Alpine.store('modals').close('cart');
  }
});

const cartChangeEventHandler = (e) => {
  const { cartItemCount, itemsRootEl, lineItemEl, variantId, key } = e.detail;

  document.dispatchEvent(
    new CustomEvent('theme:cart:change', {
      detail: {
        type: e.type,
        cartItemCount,
        itemsRootEl,
        lineItemEl,
        variantId,
        key,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener('theme:product:add', cartChangeEventHandler);
document.addEventListener('theme:line-item:change', cartChangeEventHandler);

const cartErrorEventHandler = (e) => {
  document.dispatchEvent(
    new CustomEvent('theme:cart:error', {
      detail: {
        type: e.type,
        message: e.detail.message,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener(
  'theme:product:error:add-to-cart',
  cartErrorEventHandler
);

document.addEventListener('theme:line-item:error', cartErrorEventHandler);
document.addEventListener('theme:cart:error:other', cartErrorEventHandler);

/**
 * Set `window.themeEventsDebugMode` to `true` to log events
 * outside the theme editor
 */

if (Shopify.designMode || window.themeEventsDebugMode) {
  const themeEvents = [
    'theme:variant:change',
    'theme:product:add',
    'theme:line-item:change',
    'theme:cart:change',
    'theme:cart:update',
    'theme:cart-drawer:opening',
    'theme:cart-drawer:open',
    'theme:cart-drawer:closing',
    'theme:cart-drawer:closed',
    'theme:product:error:add-to-cart',
    'theme:line-item:error',
    'theme:cart:error:other',
    'theme:cart:error',
  ];

  const aggregateEvents = ['theme:cart:change', 'theme:cart:error'];

  for (const eventName of themeEvents) {
    let color;

    if (eventName.includes('error')) {
      color = '#ef4444';
    } else {
      color = '#22c55e';
    }

    const message = [
      `%c\u25CF %c${eventName}`,
      `color: ${color};`,
      'font-weight: bold;',
    ];

    if (aggregateEvents.includes(eventName)) {
      document.addEventListener(eventName, (e) => {
        console.groupCollapsed(...message);
        console.log(
          `Underlying event: %c${e.detail.type}`,
          'font-weight: bold;'
        );
        console.log('Event detail:', e.detail);
        console.groupEnd();
      });
    } else {
      document.addEventListener(eventName, (e) => {
        console.log(...message, e.detail);
      });
    }
  }
}

// Use when unicorn is ovewritting the cart structure, if removed it can be added to the cart logic
class CartWatcher {
  constructor() {
    this.storageKey = 'cart_cache';
    this.flagKey = 'has_engrave_it';
  }

  init() {
    // Check if we already have engravings from a previous session
    const hasFlag = sessionStorage.getItem(this.flagKey) === 'true';
    
    // Start the observer, but it will be "dormant" inside
    this.observeCartChanges(hasFlag);
  }

  observeCartChanges(isAlreadyActive) {
    let isActive = isAlreadyActive;

    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      
      // 1. Detect if something is being added to the cart
      const isAdding = entries.some(e => e.name.includes('/cart/add'));
      // 2. Detect any other cart update
      const isCartRequest = entries.some(e => /\/cart\//.test(e.name));

      // If we are adding OR we already have an active flag, then we sync
      if (isAdding || (isActive && isCartRequest)) {
        this.emitCartChanges().then(hasEngrave => {
          isActive = hasEngrave; // Keep synced with reality
          sessionStorage.setItem(this.flagKey, hasEngrave);
        });
      }
    }).observe({ entryTypes: ["resource"] });
  }

  async fetchCart() {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`);
    return res.json();
  }

  async emitCartChanges() {
    const newCart = await this.fetchCart();
    const oldCart = JSON.parse(localStorage.getItem(this.storageKey)) || { items: [] };
    
    const hasEngrave = newCart.items.some(i => i.product_title === "Engrave It");
    
    // Logic to find added/removed items
    const onlyInLeft = (l, r) => l.filter(li => !r.some(ri => li.key == ri.key));
    let changes = {
      added: onlyInLeft(newCart.items, oldCart.items),
      removed: onlyInLeft(oldCart.items, newCart.items),
      currentItems: newCart.items
    };

    // Quantity diff logic
    oldCart.items.forEach(oi => {
      const ni = newCart.items.find(i => i.key == oi.key && i.quantity != oi.quantity);
      if (ni) {
        let diff = ni.quantity - oi.quantity;
        let item = { ...ni, quantity: Math.abs(diff) };
        diff > 0 ? changes.added.push(item) : changes.removed.push(item);
      }
    });

    // Only dispatch if there's actual change
    if (changes.added.length > 0 || changes.removed.length > 0) {
      window.dispatchEvent(new CustomEvent("cart_changed", { detail: changes }));
    }

    localStorage.setItem(this.storageKey, JSON.stringify(newCart));
    return hasEngrave; // Return status to update the observer's state
  }
}

new CartWatcher().init();


window.addEventListener("cart_changed", async (e) => {
  const { removed, currentItems } = e.detail;

  // 1. Calculate how many units of "Engrave It" were removed in this change
  const engraveQtyRemoved = removed
    .filter(i => i.product_title === "Engrave It")
    .reduce((sum, i) => sum + i.quantity, 0);

  if (engraveQtyRemoved > 0) {
    // 2. Count current "Engrave It" vs Items with properties
    const currentEngraveCount = currentItems
      .filter(i => i.product_title === "Engrave It")
      .reduce((sum, i) => sum + i.quantity, 0);

    const itemsWithProps = currentItems.filter(i => i.properties?.Engraving);

    // 3. Determine how many items need to be cleaned to restore balance
    let surplus = itemsWithProps.length - currentEngraveCount;

    // 4. Loop to clean the necessary amount of items sequentially
    for (let i = 0; i < surplus; i++) {
      const target = itemsWithProps[i];
      if (!target) break;

      const { Engraving, Font, Placement, ...cleanProperties } = target.properties;

      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: target.key,
          properties: cleanProperties
        })
      });
    }
  }
});